// DiagnosticDoc — editorial render of the final markdown diagnostic.
//
// The markdown is parsed into structured pieces (title, tags, synthèse,
// numbered sections, scoring criteria, verdict, sources) so the layout
// can be a real document instead of dumped HTML. The same component is
// also responsible for the PDF / print view — a separate <PrintCover>
// element is hidden on screen and revealed at print time.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import renderMarkdown from '../renderMarkdown';
import {
  FaArrowLeft, FaRedo, FaCopy, FaPrint, FaCheck, FaQuoteLeft,
} from 'react-icons/fa';

import parseDiagnostic from './parseDiagnostic';

// Sanitized: the markdown comes from an LLM (and can carry prompt-injected
// HTML), so it must never reach innerHTML unsanitized.
const md2html = (md) => (md ? renderMarkdown(md) : '');

// Strip the scoring table + global/verdict lines from a section's body so
// they aren't double-rendered (we have a dedicated component for scoring).
const stripScoringNoise = (body) =>
  (body || '')
    .replace(/^\|[^\n]*\|[^\n]*$\n?/gm, '')              // table rows
    .replace(/^\s*Score\s+global[^\n]*$\n?/gim, '')      // global line
    .replace(/^\s*\*\*Verdict[^\n]*\*\*[^\n]*$\n?/gim, '')// verdict line
    .replace(/^\s*Verdict[^\n]*$\n?/gim, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

const slug = (s = '') =>
  s.toString().toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const TAG_TONES = ['mist', 'sky', 'amber', 'lilac', 'mint'];

export default function DiagnosticDoc({ markdown, onRestart, onBack }) {
  const { t } = useTranslation();
  const parsed = useMemo(() => parseDiagnostic(markdown), [markdown]);
  const [copied, setCopied] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const articleRef = useRef(null);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(markdown || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) { /* noop */ }
  };
  const handlePrint = () => window.print();

  // Highlight TOC entry of the section currently in view.
  useEffect(() => {
    if (!articleRef.current) return;
    const nodes = articleRef.current.querySelectorAll('[data-toc-target]');
    if (!nodes.length) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-25% 0px -55% 0px', threshold: [0, 0.25, 0.6] },
    );
    nodes.forEach((n) => obs.observe(n));
    return () => obs.disconnect();
  }, [parsed]);

  if (!parsed) return null;

  const tocEntries = [
    ...(parsed.synthese ? [{ id: 'synthese', label: 'Synthèse', n: '·' }] : []),
    ...parsed.sections.map((s) => ({
      id: `s-${s.number ?? slug(s.title)}`,
      label: s.title,
      n: s.number ? String(s.number).padStart(2, '0') : '·',
    })),
    ...(parsed.sources ? [{ id: 'sources', label: parsed.sources.title, n: '·' }] : []),
  ];

  return (
    <div className="diag-doc-page">
      <header className="diag-doc-header dia-no-print">
        <button type="button" className="diag-back" onClick={onBack} aria-label={t('diagia.doc.back', 'Retour')}>
          <FaArrowLeft />
        </button>
        <div className="diag-doc-title">
          <span className="diag-eyebrow">{t('diagia.diag.eyebrow', 'Diagnostic IA')}</span>
          <h1>{t('diagia.doc.ready', 'Votre fiche est prête')}</h1>
        </div>
        <div className="diag-doc-actions">
          <button type="button" className="diag-btn diag-btn-ghost" onClick={handleCopy}>
            {copied ? <FaCheck /> : <FaCopy />} {copied ? t('diagia.doc.copied', 'Copié') : t('diagia.doc.copy', 'Copier')}
          </button>
          <button type="button" className="diag-btn diag-btn-ghost" onClick={handlePrint}>
            <FaPrint /> PDF
          </button>
          {onRestart && (
            <button type="button" className="diag-btn diag-btn-ghost" onClick={onRestart}>
              <FaRedo /> {t('diagia.doc.new', 'Nouveau')}
            </button>
          )}
        </div>
      </header>

      {/* Cover page — invisible on screen, shown at print time only */}
      <PrintCover parsed={parsed} />

      <main className="diag-doc-main">
        <div className="diag-doc-grid">
          <article ref={articleRef} className="diag-doc">
            <HeroBand parsed={parsed} />
            {parsed.synthese && <SyntheseCard synthese={parsed.synthese} />}
            {parsed.sections.map((s) => (
              <SectionBlock
                key={s.number ?? slug(s.title)}
                section={s}
                scoring={s.isScoring ? parsed.scoring : null}
              />
            ))}
            {parsed.sources && <SourcesBlock sources={parsed.sources} />}
          </article>

          <Sidebar parsed={parsed} entries={tocEntries} activeId={activeId} />
        </div>

        <footer className="diag-doc-foot dia-no-print">
          Diagnostic IA · Généré par UpyaChat
        </footer>
      </main>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Sub-components                                                     */
/* ------------------------------------------------------------------ */

function HeroBand({ parsed }) {
  const { t } = useTranslation();
  const score = parsed.scoring?.global;
  const verdict = parsed.subtitle || parsed.scoring?.verdict || '';
  return (
    <section className="diag-hero-band">
      <div className="diag-hero-band-text">
        <span className="diag-eyebrow">{t('diagia.doc.usecase', "Cas d'usage IA")}</span>
        <h1 className="diag-doc-h1">{parsed.title || 'Diagnostic IA'}</h1>
        {verdict && <p className="diag-hero-band-verdict">{verdict}</p>}
        {parsed.tags.length > 0 && (
          <ul className="diag-tag-row">
            {parsed.tags.slice(0, 8).map((t, i) => (
              <li key={t} className={`diag-tag diag-tag-${TAG_TONES[i % TAG_TONES.length]}`}>
                #{t}
              </li>
            ))}
          </ul>
        )}
      </div>
      {Number.isFinite(score) && <ScoreDonut score={score} />}
    </section>
  );
}

function ScoreDonut({ score }) {
  const { t } = useTranslation();
  // SVG donut, 0–100. Stroke-dashoffset trick for a clean reveal.
  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const verdictTone =
    score >= 80 ? 'prio' : score >= 65 ? 'mid' : score >= 50 ? 'explore' : 'low';
  return (
    <div className={`diag-score-donut diag-score-${verdictTone}`}>
      <svg viewBox="0 0 120 120" aria-hidden="true">
        <circle cx="60" cy="60" r={radius} className="diag-donut-track" />
        <circle
          cx="60" cy="60" r={radius}
          className="diag-donut-fill"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform="rotate(-90 60 60)"
        />
      </svg>
      <div className="diag-score-inner">
        <span className="diag-score-num">{score}</span>
        <span className="diag-score-den">/100</span>
        <span className="diag-score-label">{t('diagia.home.score_label', 'Score global')}</span>
      </div>
    </div>
  );
}

function SyntheseCard({ synthese }) {
  if (!synthese.items || synthese.items.length === 0) {
    return (
      <section id="synthese" data-toc-target className="diag-block diag-block-synthese">
        <h2><span className="diag-block-num">·</span> Synthèse</h2>
        <div dangerouslySetInnerHTML={{ __html: md2html(synthese.body) }} />
      </section>
    );
  }
  return (
    <section id="synthese" data-toc-target className="diag-block diag-block-synthese">
      <h2><span className="diag-block-num">·</span> Synthèse</h2>
      <dl className="diag-synthese-grid">
        {synthese.items.map((it) => (
          <div key={it.key} className="diag-synthese-cell">
            <dt>{it.key}</dt>
            <dd>{it.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function SectionBlock({ section, scoring }) {
  const id = `s-${section.number ?? slug(section.title)}`;
  const bodyToRender = scoring ? stripScoringNoise(section.body) : section.body;

  return (
    <section id={id} data-toc-target className="diag-block">
      <h2>
        <span className="diag-block-num">
          {section.number ? String(section.number).padStart(2, '0') : '·'}
        </span>
        {section.title}
      </h2>
      {bodyToRender && (
        <div
          className="diag-block-body"
          dangerouslySetInnerHTML={{ __html: md2html(bodyToRender) }}
        />
      )}
      {scoring && <ScoringChart scoring={scoring} />}
    </section>
  );
}

function ScoringChart({ scoring }) {
  if (!scoring?.criteria?.length) return null;
  return (
    <div className="diag-scoring">
      <ul className="diag-scoring-bars">
        {scoring.criteria.map((c) => (
          <li key={c.name}>
            <span className="diag-scoring-label">{c.name}</span>
            <span className="diag-scoring-track">
              <span
                className={`diag-scoring-fill diag-fill-${
                  c.score >= 8 ? 'high' : c.score >= 6 ? 'mid' : 'low'
                }`}
                style={{ width: `${c.score * 10}%` }}
              />
            </span>
            <strong className="diag-scoring-val">{c.score}/10</strong>
            <em className="diag-scoring-analysis">{c.analysis}</em>
          </li>
        ))}
      </ul>
      <div className="diag-scoring-foot">
        {Number.isFinite(scoring.global) && (
          <span className="diag-scoring-total">
            Score global estimé : <strong>{scoring.global}/100</strong>
          </span>
        )}
        {scoring.verdict && (
          <div className="diag-scoring-verdict">
            <FaQuoteLeft className="diag-quote-mark" />
            <span>{scoring.verdict}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function SourcesBlock({ sources }) {
  return (
    <section id="sources" data-toc-target className="diag-block diag-block-sources">
      <h2><span className="diag-block-num">·</span> {sources.title}</h2>
      <div dangerouslySetInnerHTML={{ __html: md2html(sources.body) }} />
    </section>
  );
}

function Sidebar({ parsed, entries, activeId }) {
  const { t } = useTranslation();
  return (
    <aside className="diag-doc-side dia-no-print">
      <SideScoreCard parsed={parsed} />
      <nav className="diag-toc">
        <span className="diag-toc-eyebrow">{t('diagia.doc.toc', 'Sommaire')}</span>
        <ul>
          {entries.map((e) => (
            <li key={e.id}>
              <a
                href={`#${e.id}`}
                className={activeId === e.id ? 'is-active' : ''}
                onClick={(ev) => {
                  ev.preventDefault();
                  document.getElementById(e.id)?.scrollIntoView({
                    behavior: 'smooth', block: 'start',
                  });
                }}
              >
                <span className="diag-toc-num">{e.n}</span>
                <span className="diag-toc-label">{e.label}</span>
              </a>
            </li>
          ))}
        </ul>
      </nav>
    </aside>
  );
}

function SideScoreCard({ parsed }) {
  const { t } = useTranslation();
  const score = parsed.scoring?.global;
  if (!Number.isFinite(score)) return null;
  return (
    <div className="diag-side-score">
      <span className="diag-eyebrow">{t('diagia.res.th_verdict', 'Verdict')}</span>
      <div className="diag-side-score-num">{score}<small>/100</small></div>
      <p>{parsed.scoring.verdict || '—'}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Print-only cover page                                              */
/* ------------------------------------------------------------------ */

function PrintCover({ parsed }) {
  const { t } = useTranslation();
  const score = parsed.scoring?.global;
  const orga = parsed.synthese?.items?.find((it) => /organisation/i.test(it.key))?.value || '';
  const today = new Date().toLocaleDateString('fr-FR', {
    day: '2-digit', month: 'long', year: 'numeric',
  });
  return (
    <aside className="diag-print-cover" aria-hidden="true">
      <header>
        <span className="diag-cover-eyebrow">{t('diagia.doc.cover', 'Diagnostic IA · Fiche de cadrage')}</span>
      </header>
      <div className="diag-cover-body">
        <h1>{parsed.title || 'Diagnostic IA'}</h1>
        {parsed.subtitle && <p className="diag-cover-sub">{parsed.subtitle}</p>}
        {parsed.tags.length > 0 && (
          <ul className="diag-cover-tags">
            {parsed.tags.slice(0, 6).map((t) => (
              <li key={t}>#{t}</li>
            ))}
          </ul>
        )}
        {Number.isFinite(score) && (
          <div className="diag-cover-score">
            <ScoreDonut score={score} />
          </div>
        )}
      </div>
      <footer>
        <span>{orga || 'Cadrage IA'}</span>
        <span>{today}</span>
      </footer>
    </aside>
  );
}
